/**
 * Hotel two-step router, PDP room selection and direct checkout, and the
 * ticket-page upsell.
 *
 * Everything is progressive enhancement. With JavaScript disabled the directory
 * renders every night's room, the PDP still posts to /cart/add with a checked
 * room, and the upsell falls back to a link to the hotel page.
 */

(function () {
  "use strict";

  /* ------------------------------------------------------------------ */
  /* Tracking                                                            */
  /* ------------------------------------------------------------------ */

  /*
   * One helper for every destination, so an event cannot be sent to one and
   * forgotten on another. Every call is guarded: a pixel may legitimately be
   * absent (no consent, a blocker, or simply not installed yet) and that must
   * never break the buy path.
   *
   * Standard Meta event names are used verbatim — ViewContent, AddToCart,
   * InitiateCheckout — because custom names do not drive the standard
   * optimisation and reporting paid traffic depends on.
   */
  function track(event, payload) {
    try {
      if (typeof window.fbq === "function") window.fbq("track", event, payload);
    } catch (e) { /* never let a pixel failure block a purchase */ }
    try {
      if (typeof window.gtag === "function") window.gtag("event", event, payload);
    } catch (e) { /* as above */ }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: "hotel_" + event }, payload));
    } catch (e) { /* as above */ }
  }

  /*
   * Pixels send beacons asynchronously. A redirect fired in the same tick can
   * cut them off, so give the network a moment — but never block the user for
   * longer than that, and never fail closed.
   */
  function afterBeacons(fn) {
    var done = false;
    var go = function () {
      if (done) return;
      done = true;
      fn();
    };
    setTimeout(go, 220);
  }

  function addToCart(items) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: items }),
    }).then(function (r) {
      if (!r.ok) throw new Error("cart/add " + r.status);
      return r.json();
    }).then(function (data) {
      /* Lets the floating basket refresh without a page load. */
      document.dispatchEvent(new CustomEvent("cart:updated"));
      return data;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Two-step router                                                     */
  /* ------------------------------------------------------------------ */

  var router = document.querySelector("[data-hotel-router]");
  if (router) {
    var select = router.querySelector("[data-hotel-select]");
    var picks = [].slice.call(router.querySelectorAll("[data-hotel-pick]"));
    var results = [].slice.call(router.querySelectorAll("[data-hotel-result]"));
    var prompt = router.querySelector("[data-hotel-prompt]");

    /* Hidden from here, not in the markup, so a no-JS visitor keeps the full
       directory instead of an empty page. */
    results.forEach(function (r) { r.hidden = true; });

    function choose(handle, scroll) {
      var found = false;

      results.forEach(function (r) {
        var match = r.dataset.hotelResult === handle;
        r.hidden = !match;
        if (match) found = true;
      });

      picks.forEach(function (b) {
        b.setAttribute("aria-pressed", b.dataset.hotelPick === handle ? "true" : "false");
      });

      if (select && select.value !== handle) select.value = handle;
      if (prompt) prompt.hidden = found;

      /* Reflect the choice in the URL so a picked date can be shared or
         returned to, without adding a history entry per click. */
      try {
        var url = new URL(window.location.href);
        if (handle) url.searchParams.set("date", handle);
        else url.searchParams.delete("date");
        window.history.replaceState(null, "", url);
      } catch (e) { /* non-fatal */ }

      if (found && scroll) {
        var panel = results.find(function (r) { return !r.hidden; });
        if (panel && panel.scrollIntoView) {
          panel.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
    }

    if (select) {
      select.addEventListener("change", function () {
        choose(select.value, true);
      });
    }

    picks.forEach(function (b) {
      b.addEventListener("click", function () {
        choose(b.dataset.hotelPick, true);
      });
    });

    /* Deep link: /pages/hotels?date=ticket-berlin-2026-10-07 */
    try {
      var wanted = new URL(window.location.href).searchParams.get("date");
      if (wanted) choose(wanted, false);
    } catch (e) { /* non-fatal */ }
  }

  /* ------------------------------------------------------------------ */
  /* Hotel PDP                                                           */
  /* ------------------------------------------------------------------ */

  var form = document.querySelector("[data-hotel-form]");
  if (form) {
    var rooms = [].slice.call(form.querySelectorAll("[data-hotel-room]"));
    var variantInput = form.querySelector("[data-hotel-variant-input]");
    var totalEl = form.querySelector("[data-hotel-total]");
    var addBtn = form.querySelector("[data-hotel-add]");
    var buyBtn = document.querySelector("[data-hotel-buynow]");

    var base = {};
    try {
      var node = document.querySelector("[data-hotel-analytics]");
      if (node) base = JSON.parse(node.textContent);
    } catch (e) { base = {}; }

    var selected = function () {
      for (var i = 0; i < rooms.length; i++) if (rooms[i].checked) return rooms[i];
      return rooms[0] || null;
    };

    var payload = function (extra) {
      var r = selected();
      var out = {
        content_ids: r ? [r.value] : base.content_ids || [],
        content_type: base.content_type || "product",
        content_name: base.content_name || document.title,
        content_category: base.content_category || "Hotel",
        currency: base.currency || "GBP",
        value: r ? Number(r.dataset.price) / 100 : base.value || 0,
        num_items: 1,
      };
      if (base.city) out.city = base.city;
      if (base.show_date) out.show_date = base.show_date;
      return Object.assign(out, extra || {});
    };

    var sync = function () {
      var r = selected();
      if (!r) return;
      if (variantInput) variantInput.value = r.value;
      if (totalEl) totalEl.textContent = r.dataset.priceFormatted;
      if (addBtn) {
        addBtn.disabled = r.disabled;
        addBtn.textContent = r.disabled ? "Sold out" : "Add to cart";
      }
      if (buyBtn) buyBtn.disabled = r.disabled;
    };

    rooms.forEach(function (r) { r.addEventListener("change", sync); });
    sync();

    track("ViewContent", payload());

    form.addEventListener("submit", function () {
      track("AddToCart", payload());
    });

    /*
     * Buy now: straight past /cart into Shopify checkout with the chosen room
     * already on the order.
     *
     * Both events fire before the redirect, because once the browser leaves for
     * checkout nothing further on this page will run. AddToCart is sent as well
     * as InitiateCheckout — skipping it would leave a hole in the funnel for
     * every buyer who takes the fast path, which is exactly the traffic the ads
     * are optimising for.
     */
    if (buyBtn) {
      buyBtn.addEventListener("click", function (e) {
        e.preventDefault();
        var r = selected();
        if (!r || r.disabled) return;

        var p = payload();
        track("AddToCart", p);
        track("InitiateCheckout", p);

        buyBtn.disabled = true;
        buyBtn.textContent = "Taking you to checkout…";

        addToCart([{ id: Number(r.value), quantity: 1 }])
          .then(function () {
            afterBeacons(function () { window.location.href = "/checkout"; });
          })
          .catch(function () {
            /* Fall back to the normal path rather than stranding the buyer. */
            buyBtn.disabled = false;
            buyBtn.textContent = "Buy now";
            form.submit();
          });
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Ticket PDP upsell                                                   */
  /* ------------------------------------------------------------------ */

  var upsell = document.querySelector("[data-hotel-upsell]");
  if (!upsell) return;

  var roomBtns = [].slice.call(upsell.querySelectorAll("[data-upsell-room]"));
  var addRoom = upsell.querySelector("[data-upsell-add]");
  if (!addRoom) return;

  var chosen = roomBtns.length ? roomBtns[0].dataset.upsellRoom : addRoom.dataset.variant;

  var upsellPrice = upsell.querySelector("[data-upsell-price]");

  roomBtns.forEach(function (b) {
    b.addEventListener("click", function () {
      chosen = b.dataset.upsellRoom;
      roomBtns.forEach(function (o) {
        o.setAttribute("aria-pressed", o === b ? "true" : "false");
      });

      /* The headline price has to follow the selection. Without this it stayed
         on the cheapest room while the cart charged for the chosen one — the
         two disagreed on screen. data-price-formatted is already a money
         string from Liquid, so no currency formatting is guessed at here. */
      if (upsellPrice && b.dataset.priceFormatted) {
        upsellPrice.textContent = b.dataset.priceFormatted;
      }
    });
  });

  addRoom.addEventListener("click", function () {
    if (!chosen) return;

    var btn = roomBtns.filter(function (b) { return b.dataset.upsellRoom === chosen; })[0];
    var price = btn ? Number(btn.dataset.price) / 100 : Number(addRoom.dataset.price) / 100;

    track("AddToCart", {
      content_ids: [chosen],
      content_type: "product",
      content_name: upsell.dataset.hotelName || "Hotel room",
      content_category: "Hotel",
      currency: upsell.dataset.currency || "GBP",
      value: price || 0,
      num_items: 1,
      source: "ticket_pdp_upsell",
    });

    addRoom.disabled = true;
    addRoom.textContent = "Adding…";

    addToCart([{ id: Number(chosen), quantity: 1 }])
      .then(function () {
        addRoom.textContent = "Room added ✓";
      })
      .catch(function () {
        addRoom.disabled = false;
        addRoom.textContent = "Add room to order";
      });
  });
})();
